import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import camiyuiLogo from '../../assets/camiyui-logo.jpg';
import { useI18n } from '../../i18n';

const StaffIntakeQrPage: React.FC = () => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();

  const source = searchParams.get('source') || 'admin_qr';
  const storeId = Number(searchParams.get('storeId'));
  const reservationId = Number(searchParams.get('reservationId'));
  const sessionId = Number(searchParams.get('selfRegisterSessionId'));
  const customerId = Number(searchParams.get('customerId'));

  const hasStoreId = Number.isFinite(storeId) && storeId > 0;
  const hasReservationId = Number.isFinite(reservationId) && reservationId > 0;
  const hasSessionId = Number.isFinite(sessionId) && sessionId > 0;
  const hasCustomerId = Number.isFinite(customerId) && customerId > 0;

  const registrationUrl = useMemo(() => {
    if (!hasStoreId || !hasSessionId) return '';
    const url = new URL('/registration', window.location.origin);
    url.searchParams.set('source', source);
    url.searchParams.set('storeId', String(storeId));
    if (hasReservationId) {
      url.searchParams.set('reservationId', String(reservationId));
    }
    if (hasCustomerId) {
      url.searchParams.set('customerId', String(customerId));
    }
    url.searchParams.set('selfRegisterSessionId', String(sessionId));
    return url.toString();
  }, [customerId, hasCustomerId, hasReservationId, hasSessionId, hasStoreId, reservationId, sessionId, source, storeId]);

  const qrImageUrl = useMemo(() => {
    if (!registrationUrl) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&data=${encodeURIComponent(
      registrationUrl
    )}`;
  }, [registrationUrl]);

  if (!registrationUrl) {
    return (
      <div className="min-h-screen bg-zinc-100 p-4">
        <div className="mx-auto max-w-lg rounded-2xl border border-rose-200 bg-white p-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-rose-700">{t('intake.invalidTitle')}</p>
          <p className="mt-2 text-xs text-zinc-600">
            {t('intake.invalidDetail')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 px-3 py-4">
      <main className="mx-auto max-w-xl">
        <section className="overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-lg shadow-emerald-100/60">
          <div className="border-b border-emerald-200 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 px-5 py-5 text-white">
            <div className="mb-2 flex items-center gap-3">
              <div className="h-10 w-10 overflow-hidden rounded-full border border-white/40 bg-white shadow-sm">
                <img src={camiyuiLogo} alt="Camiyui" className="h-full w-full object-cover" />
              </div>
              <h1 className="text-lg font-semibold">{t('intake.title')}</h1>
            </div>
            {hasReservationId && (
              <p className="text-sm font-semibold text-emerald-100">
                {t('intake.reservationId', { id: reservationId })}
              </p>
            )}
          </div>

          <div className="space-y-3 p-5 text-center">
            <p className="text-sm text-zinc-700">
              {t('intake.instructions')}
            </p>
            <div className="mx-auto w-fit rounded-2xl border border-emerald-100 bg-white p-3 shadow-sm">
              <img src={qrImageUrl} alt={t('intake.qrAlt')} className="h-[320px] w-[320px]" />
            </div>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-left">
              <p className="text-xs font-semibold text-zinc-600">{t('intake.urlLabel')}</p>
              <p className="mt-1 break-all text-xs text-zinc-700">{registrationUrl}</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default StaffIntakeQrPage;
